import { useEffect, useState } from 'react';
import { Layout, Typography, Button, Dropdown, Avatar, Menu, Modal } from 'antd';
import {
  DashboardOutlined,
  LogoutOutlined,
  UserOutlined,
  MenuOutlined,
  PlusOutlined,
  HistoryOutlined,
  MessageOutlined,
  FileSearchOutlined,
  LockOutlined,
} from '@ant-design/icons';
import type { MenuProps } from 'antd';
import { useNavigate, useLocation, Outlet } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuthUser, signOut, getCurrentOrgId, supabase } from '@ubus/shared';
import ClientSOSButton from './ClientSOSButton';
import LanguageSwitcher from './LanguageSwitcher';

const { Header, Content, Sider } = Layout;
const { Text } = Typography;

function ClientLayout() {
  const { t } = useTranslation();
  const user = useAuthUser();
  const [collapsed, setCollapsed] = useState(false);
  const [orgName, setOrgName] = useState<string | null>(null);
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const orgId = await getCurrentOrgId();
      if (!orgId || cancelled) return;
      const { data } = await supabase
        .from('organizations')
        .select('name')
        .eq('id', orgId)
        .maybeSingle();
      if (!cancelled && data?.name) setOrgName(data.name);
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const handleLogout = () => {
    Modal.confirm({
      title: t('nav.signOutTitle'),
      content: t('nav.signOutBody'),
      okText: t('nav.signOut'),
      okType: 'danger',
      cancelText: t('common.stay'),
      onOk: async () => {
        await signOut();
        navigate('/login');
      },
    });
  };

  const sidebarItems: MenuProps['items'] = [
    { key: '/dashboard', icon: <DashboardOutlined />, label: t('nav.home') },
    { key: '/requests', icon: <FileSearchOutlined />, label: t('nav.requests') },
    { key: '/trips', icon: <HistoryOutlined />, label: t('nav.trips') },
    { key: '/feedback', icon: <MessageOutlined />, label: t('nav.feedback') },
  ];

  const userMenuItems: MenuProps['items'] = [
    { key: 'account', icon: <LockOutlined />, label: t('nav.changePassword') },
    { key: 'logout', icon: <LogoutOutlined />, label: t('nav.signOut'), danger: true },
  ];

  const getSelectedKey = () => {
    const path = location.pathname;
    if (path.includes('dashboard')) return ['/dashboard'];
    if (path.includes('requests') || path.includes('book')) return ['/requests'];
    if (path.includes('history') || path.includes('trips') || path.includes('trip') || path.includes('map')) {
      return ['/trips'];
    }
    if (path.includes('feedback')) return ['/feedback'];
    return ['/dashboard'];
  };

  const getCurrentTripId = (): string | undefined => {
    const match = location.pathname.match(/\/(trip|map)\/([0-9a-f-]+)/);
    return match ? match[2] : undefined;
  };

  return (
    <Layout className="client-shell" style={{ minHeight: '100vh' }}>
      <Header
        style={{
          padding: '0 20px',
          height: 64,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          position: 'sticky',
          top: 0,
          zIndex: 100,
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, minWidth: 0 }}>
          <Button
            type="text"
            icon={<MenuOutlined />}
            onClick={() => setCollapsed(!collapsed)}
            aria-label={t('common.toggleNav')}
          />
          <Text
            strong
            style={{
              fontSize: 22,
              color: 'var(--client-brand)',
              fontFamily: 'var(--client-font-display)',
              letterSpacing: '-0.02em',
            }}
          >
            UBUS
          </Text>
          <Text type="secondary" style={{ fontSize: 13, whiteSpace: 'nowrap' }}>
            {t('nav.brandTagline')}
          </Text>
          {orgName && (
            <Text
              ellipsis
              style={{
                fontSize: 13,
                color: 'var(--client-ink-secondary)',
                maxWidth: 280,
                borderLeft: '1px solid var(--client-border)',
                paddingLeft: 12,
              }}
            >
              {orgName}
            </Text>
          )}
        </div>

        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <Button
            type="primary"
            size="middle"
            icon={<PlusOutlined />}
            onClick={() => navigate('/requests/new')}
          >
            {t('nav.newRequest')}
          </Button>
          <ClientSOSButton tripId={getCurrentTripId()} compact />
          <LanguageSwitcher size="middle" />
          <Dropdown
            menu={{
              items: userMenuItems,
              onClick: ({ key }) => {
                if (key === 'account') navigate('/account');
                if (key === 'logout') handleLogout();
              },
            }}
            placement="bottomRight"
          >
            <div
              style={{ cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 8, padding: '4px 0' }}
              role="button"
              tabIndex={0}
            >
              <Avatar size="small" icon={<UserOutlined />} style={{ background: 'var(--client-brand)' }} />
              <Text style={{ fontSize: 13, maxWidth: 160 }} ellipsis>
                {user?.email || t('common.clientRole')}
              </Text>
            </div>
          </Dropdown>
        </div>
      </Header>

      <Layout>
        <Sider
          collapsible
          collapsed={collapsed}
          onCollapse={setCollapsed}
          trigger={null}
          width={220}
          collapsedWidth={72}
        >
          <Menu
            mode="inline"
            selectedKeys={getSelectedKey()}
            items={sidebarItems}
            onClick={({ key }) => navigate(key)}
            style={{ border: 'none', marginTop: 12 }}
          />
        </Sider>

        <Content style={{ margin: 20, overflow: 'auto' }}>
          <div className="client-content-card">
            <Outlet />
          </div>
        </Content>
      </Layout>
    </Layout>
  );
}

export default ClientLayout;
